import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';

/**
 * ClusterAnalysis - Shows faces detected in capture with match info
 * Mobile-first, compact design for event detail dialog
 */
export default function ClusterAnalysis({ clusterData, loading, error }) {
  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Loading cluster analysis...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return null; // Silently fail - not critical
  }

  if (!clusterData?.clusterMetadata) {
    return null;
  }

  const { clusterMetadata, associatedPersonsFound } = clusterData;
  const { total_faces_detected, clusters } = clusterMetadata;

  // Count match types across all faces
  const counts = { known: 0, associated: 0, unknown: 0 };
  clusters.forEach(cluster => {
    cluster.faces.forEach(face => {
      if (face.match?.type === 'known') counts.known++;
      else if (face.match?.type === 'associated') counts.associated++;
      else counts.unknown++;
    });
  });

  return (
    <Accordion
      defaultExpanded={false}
      sx={{
        mt: 2,
        '&:before': { display: 'none' },
        boxShadow: 1
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <GroupIcon fontSize="small" color="action" />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {total_faces_detected} {total_faces_detected === 1 ? 'face' : 'faces'} detected
          </Typography>
          {counts.known > 0 && (
            <Chip
              label={`${counts.known} known`}
              size="small"
              color="success"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {counts.associated > 0 && (
            <Chip
              label={`${counts.associated} associated`}
              size="small"
              color="info"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
          {counts.unknown > 0 && (
            <Chip
              label={`${counts.unknown} unknown`}
              size="small"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        {clusters.map((cluster, clusterIdx) => (
          <Box key={cluster.cluster_id} sx={{ mb: clusterIdx < clusters.length - 1 ? 2 : 0 }}>
            {clusters.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Cluster {cluster.cluster_index + 1} ({cluster.face_count} {cluster.face_count === 1 ? 'face' : 'faces'})
              </Typography>
            )}

            {cluster.faces.map((face, faceIdx) => {
              const match = face.match;
              const isKnown = match?.type === 'known';
              const isAssociated = match?.type === 'associated';

              return (
                <Box
                  key={faceIdx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    py: 0.5,
                    px: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: isKnown ? 'success.light' : isAssociated ? 'info.light' : 'action.hover',
                    opacity: isKnown ? 1 : isAssociated ? 0.9 : 0.7
                  }}
                >
                  <PersonIcon
                    fontSize="small"
                    sx={{
                      color: isKnown ? 'success.dark' : isAssociated ? 'info.dark' : 'text.secondary'
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {isKnown && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Person {match.personId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {match.similarity?.toFixed(1)}% match
                        </Typography>
                      </>
                    )}

                    {isAssociated && (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Associated Person
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {match.similarity?.toFixed(1)}% match
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <VisibilityIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {match.sightingCount}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}

                    {!match && (
                      <Typography variant="body2" color="text.secondary">
                        Unknown
                      </Typography>
                    )}
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.65rem'
                    }}
                  >
                    {(face.confidence || 0).toFixed(1)}%
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
